import { useRef, useState, useEffect, Fragment } from 'react'
import * as m from 'motion/react-m'
import { Motion } from '@/lib/motion/lazy-motion'
import ScrollView from '@/components/ui/ScrollView'
import AppIcon from '@/components/content/app/AppIcon'
import EmptyResults from './EmptyResults'
import { Icon } from '@/icons/Icon'
import { useChatContext } from '@/context/ChatContext'
import { ResultItemProps } from '@/types/app'
import { easeMedium } from '@/lib/transitions'
import { cn, iconMap } from '@/lib/utils'

type SpotlightProps = {
  type: 'chat' | 'nav' | 'mobile'
}

type ResultProps = {
  result: ResultItemProps
  focus: boolean
  isLast: boolean
}

export default function Spotlight({ type }: SpotlightProps) {
  const [height, setHeight] = useState(0)

  const spotlight = useRef<HTMLDivElement | null>(null)
  const itemsRef = useRef<HTMLDivElement[]>([])
  const ignoreHoverRef = useRef(false)

  const { results, handleResultClick, handleShortcutClick, selectedIndex, setSelectedIndex } =
    useChatContext()

  useEffect(() => {
    if (spotlight.current) {
      setTimeout(
        () => {
          setHeight(spotlight.current?.scrollHeight || 0)
        },
        type === 'nav' ? 250 : 0,
      )
    }
  }, [type, results])

  useEffect(() => {
    if (selectedIndex > 0) {
      itemsRef.current[selectedIndex]?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      })
    }
  }, [selectedIndex])

  useEffect(() => {
    ignoreHoverRef.current = true
  }, [results])

  useEffect(() => {
    const handleMouseMove = () => {
      if (ignoreHoverRef.current) {
        ignoreHoverRef.current = false
      }
    }

    window.addEventListener('mousemove', handleMouseMove)
    return () => window.removeEventListener('mousemove', handleMouseMove)
  }, [])

  const content = (
    <ScrollView
      direction="vertical"
      className={cn('flex h-full max-h-[calc(100vh-80px)] w-full flex-col p-2 md:max-h-[330px]')}
    >
      {results.length > 0 ? (
        results.map((result, index) => {
          const focus = selectedIndex === index
          const isLast = index === results.length - 1

          return (
            <Fragment key={`${result.label}-${index}`}>
              {result.label === '/review' && (
                <span className="flex h-8 shrink-0 items-center pl-1 text-14 font-medium text-gray-950 dark:text-white md:text-12">
                  Shortcuts
                </span>
              )}

              <div
                ref={(el) => {
                  itemsRef.current[index] = el as HTMLDivElement
                }}
                onMouseEnter={() => {
                  if (ignoreHoverRef.current) return
                  setSelectedIndex(index)
                }}
                onClick={() =>
                  result.shortcut ? handleShortcutClick(result.type) : handleResultClick(result)
                }
              >
                <Result result={result} focus={focus} isLast={isLast} />
              </div>
            </Fragment>
          )
        })
      ) : (
        <EmptyResults condensed={true} />
      )}
    </ScrollView>
  )

  if (type === 'mobile') {
    return <div className="hide-scrollbar w-full">{content}</div>
  }

  return (
    <Motion>
      <m.div
        initial={{ height: 0 }}
        animate={{ height: height }}
        exit={{ height: 0 }}
        transition={easeMedium}
        className="hide-scrollbar w-full overflow-hidden border-t border-gray-100 dark:border-white/10"
      >
        <div ref={spotlight}>{content}</div>
      </m.div>
    </Motion>
  )
}

export function Result({ result, focus, isLast }: ResultProps) {
  const { context } = useChatContext()

  function getFallbackIcon() {
    if (result.type === 'app') {
      return <AppIcon size={22} icon={result.app?.icon} glyph={result.app?.glyph} />
    }
    return <Icon name={iconMap[result.type] || 'magnifier-small'} size={12} />
  }

  return (
    <div
      className={cn(
        'group flex h-fit w-full cursor-pointer items-center gap-2.5 rounded-b-lg rounded-t-lg py-1.5 pl-0.5 text-15 text-gray-700 transition-colors dark:text-white md:py-0.5 md:text-13',
        focus && 'bg-gray-100 dark:bg-white/10',
        isLast && 'rounded-b-2xl',
      )}
    >
      <div
        className={cn(
          'flex h-8 w-8 shrink-0 items-center justify-center text-gray-400 transition-colors dark:text-gray-600',
        )}
      >
        {result.icon || getFallbackIcon()}
      </div>
      <div className="flex w-full items-center gap-1.5">
        {result.type !== 'search' && context === null && (
          <span className="font-medium leading-snug">{`/${result.type}`}</span>
        )}
        {result.label && <span className="leading-snug">{result.label}</span>}
        {/*result.apps &&
          result.apps.length > 0 &&
          (() => {
            const lowered = result.apps.map((a) => `@${a.toLowerCase()}`)
            const formatted =
              lowered.length === 1
                ? lowered[0]
                : lowered.length === 2
                  ? `${lowered[0]} and ${lowered[1]}`
                  : `${lowered.slice(0, -1).join(', ')} and ${lowered[lowered.length - 1]}`
            return <span>{formatted}</span>
          })()*/}
      </div>
      <div
        className={cn(
          'flex h-8 w-8 shrink-0 items-center justify-center text-gray-500 opacity-0 transition-opacity',
          focus && 'opacity-100',
        )}
      >
        <Icon name="return-key" size={12} />
      </div>
    </div>
  )
}
